import { useState } from 'react'
import { Plus, Heart, Eye, Flame } from 'lucide-react'
import type { MenuItem } from '@/types'
import { getTranslation } from '@/lib/i18n'
import { formatPrice, formatFromPrice } from '@/lib/formatPrice'
import { getTextAlign } from '@/lib/rtl'
import type { Language } from '@/types'

interface ItemCardProps {
  item: MenuItem
  language: Language
  isRTL: boolean
  hasPhotos: boolean
  hasCart: boolean
  onAdd?: () => void
  onToggleFavorite?: () => void
  isFavorite?: boolean
  onViewPhoto?: () => void
  className?: string
  /** position in list, used to stagger the entrance animation (capped internally) */
  index?: number
}

export function ItemCard({
  item,
  language,
  isRTL,
  hasPhotos,
  hasCart,
  onAdd,
  onToggleFavorite,
  isFavorite = false,
  onViewPhoto,
  className = '',
  index = 0,
}: ItemCardProps) {
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)

  const itemName = getTranslation(
    { fr: item.name_fr, ar: item.name_ar, en: item.name_en },
    language
  )
  const itemDesc = getTranslation(
    { fr: item.desc_fr, ar: item.desc_ar, en: item.desc_en },
    language
  )

  const hasVariants = item.variants && item.variants.length > 0
  const displayPrice = hasVariants ? item.base_price || item.price : item.price

  // Cap stagger so large menus don't make late items feel sluggish
  const delay = Math.min(index, 8) * 45

  // Placeholder for missing photos
  const placeholder = (
    <div className="img-placeholder w-full h-full">
      <span className="text-3xl sm:text-4xl">☕</span>
    </div>
  )

  const showMedia = hasPhotos && item.photo && !imageError

  return (
    <article
      className={`card group flex sm:flex-col animate-fade-up
        transition-[box-shadow,transform] duration-300 ease-out-expo
        hover:shadow-card-hover hover:-translate-y-0.5
        ${className}`}
      dir={isRTL ? 'rtl' : 'ltr'}
      style={{ animationDelay: `${delay}ms` }}
    >
      {/* ── Media ───────────────────────────────────────── */}
      <div
        className="relative shrink-0 overflow-hidden bg-secondary/20
          w-24 sm:w-full aspect-square sm:aspect-[4/3]"
      >
        {/* Skeleton while loading */}
        {showMedia && !imageLoaded && (
          <div className="absolute inset-0 skeleton" />
        )}

        {showMedia ? (
          <img
            src={item.photo}
            alt={itemName}
            className="w-full h-full object-cover transition-transform duration-500 ease-out-expo group-hover:scale-105"
            onLoad={() => setImageLoaded(true)}
            onError={() => {
              setImageError(true)
              setImageLoaded(true)
            }}
            loading="lazy"
          />
        ) : (
          placeholder
        )}

        {/* Popular badge */}
        {item.popular && (
          <div className="absolute top-1.5 left-1.5 sm:top-2 sm:left-2 flex items-center gap-1 px-2 py-0.5 sm:py-1 rounded-full bg-accent text-white text-[10px] sm:text-xs font-semibold shadow-sm">
            <Flame size={11} fill="currentColor" />
            <span>
              {language === 'fr' && 'Populaire'}
              {language === 'ar' && 'شائع'}
              {language === 'en' && 'Popular'}
            </span>
          </div>
        )}

        {/* Favorite */}
        {onToggleFavorite && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onToggleFavorite()
            }}
            className={`absolute top-1.5 right-1.5 sm:top-2 sm:right-2 p-1.5 sm:p-2 rounded-full bg-white/85 backdrop-blur-sm shadow-sm hover:bg-white transition-colors touch-feedback ${
              isFavorite ? 'animate-badge-pop' : ''
            }`}
            aria-label="Toggle favorite"
          >
            <Heart
              size={16}
              className={isFavorite ? 'fill-red-500 text-red-500' : 'text-muted-foreground'}
            />
          </button>
        )}

        {/* View photo */}
        {onViewPhoto && imageLoaded && !imageError && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onViewPhoto()
            }}
            className="absolute bottom-1.5 right-1.5 sm:bottom-2 sm:right-2 p-1.5 sm:p-2 rounded-full bg-black/45 backdrop-blur-sm hover:bg-black/65 transition-colors touch-feedback"
            aria-label="View photo"
          >
            <Eye size={15} className="text-white" />
          </button>
        )}
      </div>

      {/* ── Content ─────────────────────────────────────── */}
      <div className="flex-1 min-w-0 flex flex-col p-3 sm:p-4">
        <h3 className="font-heading font-semibold text-base sm:text-lg text-primary leading-snug line-clamp-2">
          {itemName}
        </h3>

        {itemDesc && (
          <p className="mt-0.5 sm:mt-1 text-xs sm:text-sm text-muted-foreground line-clamp-1 sm:line-clamp-2">
            {itemDesc}
          </p>
        )}

        {/* Variants indicator */}
        {hasVariants && item.variants && item.variants.length > 0 && (
          <p className="mt-1 text-[11px] sm:text-xs text-muted-foreground">
            {item.variants.length}{' '}
            {language === 'fr' && 'tailles'}
            {language === 'ar' && 'أحجام'}
            {language === 'en' && 'sizes'}
          </p>
        )}

        {/* Spacer pushes price row to the bottom on cards */}
        <div className="flex-1 min-h-2" />

        {/* Price + add */}
        <div className={`mt-2 sm:mt-3 flex items-center justify-between gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className={getTextAlign(language)}>
            {hasVariants ? (
              <span className="block text-xs sm:text-sm text-muted-foreground">
                {formatFromPrice(displayPrice, language)}
              </span>
            ) : (
              <span className="block font-heading font-bold text-lg sm:text-xl text-primary tnum">
                {formatPrice(displayPrice)}
              </span>
            )}
          </div>

          {hasCart && onAdd && (
            <button
              onClick={onAdd}
              className="flex items-center justify-center h-11 w-11 sm:h-10 sm:w-10 rounded-full bg-primary text-white shadow-sm hover:bg-primary/90 hover:shadow-md transition-all touch-feedback active:scale-90"
              aria-label="Add to cart"
            >
              <Plus size={20} strokeWidth={2.5} />
            </button>
          )}
        </div>

        {/* Unavailable */}
        {!item.available && (
          <div className="mt-3 text-center py-2 bg-muted text-muted-foreground text-sm rounded-button">
            {language === 'fr' && 'Non disponible'}
            {language === 'ar' && 'غير متوفر'}
            {language === 'en' && 'Unavailable'}
          </div>
        )}
      </div>
    </article>
  )
}

// ============================================================================
// Item Card Skeleton (Loading State)
// ============================================================================

export function ItemCardSkeleton({ className = '' }: { className?: string }) {
  return (
    <div className={`card flex sm:flex-col ${className}`}>
      <div className="w-24 sm:w-full aspect-square sm:aspect-[4/3] skeleton shrink-0" />
      <div className="flex-1 p-3 sm:p-4 space-y-2.5">
        <div className="h-5 skeleton rounded w-3/4" />
        <div className="h-4 skeleton rounded w-full" />
        <div className="h-4 skeleton rounded w-1/3" />
      </div>
    </div>
  )
}
